import React from 'react';
import envConfig from '@nile/home/src/Config/env';
import { BackgroundIcon } from '@nile/components';
import { SVGIcon } from '@superops/super-icons';

const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  fontWeight: 'bold',
  fontSize: '14'
};

const backgroundColorMap = {
  Cancelled: '#E0E0E0',
  Blocked: '#F7C1C1'
};

const getBackgroundColor = (status) => {
  const defaultColor = 'var(--sops-light-blue)';
  if (status) {
    return backgroundColorMap?.[status] || defaultColor;
  }
};

const statusStylesObject = {
  control: (provided, state) => ({
    ...provided,
    border: 'none',
    boxShadow: 'none'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    textTransform: 'uppercase',
    borderRadius: '4px',
    padding: '0px 8px',
    fontSize: '1.2rem',
    fontWeight: '700',
    color: '#00000099',
    marginLeft: '1rem',
    marginRight: '1rem',
    height: '2rem',
    display: 'flex',
    cursor: 'pointer'
  }),
  singleValue: (provided, state) => ({
    ...provided,
    background: !state?.isDisabled && getBackgroundColor(state?.children),
    padding: '0 10px',
    marginLeft: '-10px',
    borderRadius: '10px',
    height: ' 25px',
    paddingTop: '2px',
    width: '100px',
    textAlign: 'center'
  }),
  menu: (provided, state) => ({
    ...provided,
    zIndex: '999'
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

const formatGroupLabel = (data) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
  </div>
);

const indicatorSeparatorStyle = {
  width: 0
};

const IndicatorSeparator = ({ innerProps }) => {
  return <span style={indicatorSeparatorStyle} {...innerProps} />;
};

const DropdownIndicator = (props) => {
  return (
    <div className="fx center mr8" {...props}>
      <SVGIcon
        name="ArrowDown"
        color={props?.color ?? 'var(--sops-black-white-bg)'}
      />
    </div>
  );
};

const DropdownCaretIndicator = () => {
  return (
    <img
      src={envConfig.imageRoot + 'svg/caret-down.svg'}
      style={{ color: 'black' }}
    ></img>
  );
};

/**
 * Render template or custom option template for react-select
 */

const formatOptionLabel = (props) => {
  const { name, label, template } = props;
  return template ? (
    { template }
  ) : (
    <div className="react-select-option" data-qa={name}>
      <div className="react-select-label">{label}</div>
    </div>
  );
};

const optionStyles = () => {
  return {
    menu: (provided, state) => ({
      ...provided,
      background: '#FFFFFF',
      boxShadow: '1px 2px 25px rgba(0, 0, 0, 0.152945)',
      borderRadius: 8
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '8px 20px',
      fontSize: 14,
      fontWeight: 500,
      cursor: 'pointer',
      color: state.isSelected ? 'white' : 'rgba(0, 0, 0, 0.85)',
      '&:hover': {
        backgroundColor: state.isSelected ? '#2684FF' : '#F1F6FF'
      }
    })
  };
};

const controlStyles = (config) => {
  const { minWidth, maxWidth } = config;
  return {
    control: () => ({
      minWidth: minWidth ? minWidth : 75,
      maxWidth: maxWidth ? maxWidth : 500,
      display: 'flex',
      borderBottom: '1px solid transparent !important',
      cursor: 'pointer',
      '&:hover': {
        borderBottom: '1px solid #0B0114 !important',
        borderBottomLeftRadius: 'none',
        borderBottomRightRadius: 'none'
      }
    })
  };
};

const dropdownIndicatorStyles = {
  dropdownIndicator: () => ({
    color: 'gray'
  })
};

const singleSelectStyles = (config) => {
  const { isBold = true } = config || {};
  return {
    ...controlStyles(config || {}),
    ...optionStyles(),
    ...dropdownIndicatorStyles,
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = 'opacity 300ms';
      return {
        ...provided,
        opacity,
        transition,
        fontWeight: isBold ? 500 : 'normal',
        fontSize: 14,
        lineHeight: 21,
        color: 'rgba(0, 0, 0, 0.8)'
      };
    }
  };
};

const multiSelectStyles = (config) => {
  return {
    ...controlStyles(config || {}),
    ...optionStyles(),
    ...dropdownIndicatorStyles,
    multiValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = 'opacity 300ms';
      return {
        ...provided,
        opacity,
        transition,
        backgroundColor: 'rgba(196, 196, 196, 0.2)',
        display: 'flex',
        alignItems: 'center',
        height: 24,
        fontWeight: 500,
        fontSize: 14,
        lineHeight: 21,
        color: 'rgba(0, 0, 0, 0.8)'
      };
    },
    multiValueRemove: () => ({
      color: '#323232',
      opacity: 0.4,
      fontSize: 20,
      marginRight: 4,
      cursor: 'pointer'
    })
  };
};

const CustomMenu = (props) => {
  const {
    innerRef,
    innerProps,
    isDisabled,
    children,
    actionComponent,
    showEmail
  } = props;
  return !isDisabled ? (
    <div
      ref={innerRef}
      {...innerProps}
      className={`customReactSelectMenu ${showEmail ? 'showEmail' : ''}`}
    >
      {children}
      <div className="p10">{actionComponent}</div>
    </div>
  ) : null;
};

export {
  formatGroupLabel,
  indicatorSeparatorStyle,
  IndicatorSeparator,
  DropdownIndicator,
  formatOptionLabel,
  statusStylesObject,
  multiSelectStyles,
  singleSelectStyles,
  DropdownCaretIndicator,
  CustomMenu
};
